// v15
// Path: frontend/src/components/SyncHub/shared/MappingDesignerTable.jsx
// Inputs: isStandardMode (bool), selectedQBEntity, activeTableName, mappings, definitions, sourceColumns, onAddMapping, handleCustomMappingChange, handleRemoveCustomRow, handleResetMappingRow, fieldSearch, setFieldSearch, mappingFilter, setMappingFilter
// Outputs: 5-column Tree Grid field mapping table with inline ghost actions, AG Grid search/funnel filters, symmetric indentation, and dynamic sticky elevation shadow
// Summary: Ultra-condensed 5-column data grid for field mappings featuring Column 5 inline ghost actions, text-xs softened typography, symmetric pl-10/pr-10 child row nesting, dynamic sticky elevation shadow, and pessimistic deletion flows.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import SearchableSelect from '../SearchableSelect';
import { useSyncStore } from '../useSyncStore';

export default function MappingDesignerTable(props) {
    const {
        isStandardMode = false,
        selectedQBEntity = 'Customer',
        qbSampleRecords = [],
        activeQbSampleIndex = 0,
        setActiveQbSampleIndex = () => {},
        pbsSampleRecords = [],
        activeSampleIndex = 0,
        setActiveSampleIndex = () => {},
        activeTableName = '',
        mappings = [],
        definitions = null,
        sourceColumns = [],
        onAddMapping = () => {},
        handleCustomMappingChange = () => {},
        handleRemoveCustomRow = () => {},
        handleResetMappingRow = () => {},
        fieldSearch = '',
        setFieldSearch,
        mappingFilter: propMappingFilter = '',
        setMappingFilter: propSetMappingFilter
    } = props;

    // Dynamic Sticky Elevation State for Tier 3 Field Column Header (sticks at 128px)
    const [isStuck, setIsStuck] = useState(false);
    const stickyHeaderRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (stickyHeaderRef.current) {
                const scrollContainer = stickyHeaderRef.current.closest('.overflow-y-auto') || window;
                const containerTop = (scrollContainer && scrollContainer !== window && scrollContainer.getBoundingClientRect)
                    ? scrollContainer.getBoundingClientRect().top
                    : 0;
                const headerTop = stickyHeaderRef.current.getBoundingClientRect().top - containerTop;
                const isScrolled = scrollContainer !== window 
                    ? (scrollContainer.scrollTop > 0)
                    : ((window.scrollY || window.pageYOffset || 0) > 0);

                // If the element's top coordinate is <= its sticky threshold, it is stuck!
                // Use 128 for Tier 3. Add a 1px buffer to account for fractional pixels (<= 129).
                setIsStuck((headerTop <= 129 || stickyHeaderRef.current.getBoundingClientRect().top <= 129) && isScrolled);
            }
        };

        const scrollContainer = stickyHeaderRef.current?.closest('.overflow-y-auto') || window;
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    // Double-click label inline editing state
    const [editingLabelTarget, setEditingLabelTarget] = useState(null);
    const [tempLabelValue, setTempLabelValue] = useState('');

    // Expandable search state in Tier 3 Column 1 Header
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [localSearchFilter, setLocalSearchFilter] = useState('');
    const [localMappingFilter, setLocalMappingFilter] = useState('all'); // 'all' | 'mapped' | 'unmapped'

    const effectiveSearch = fieldSearch !== undefined && fieldSearch !== '' ? fieldSearch : localSearchFilter;
    const handleSearchChange = (val) => {
        if (setFieldSearch) setFieldSearch(val);
        else setLocalSearchFilter(val);
    };

    const effectiveFilter = (propMappingFilter !== undefined && propMappingFilter !== '' ? propMappingFilter : localMappingFilter) || 'all';
    const handleFilterCycle = () => {
        const next = effectiveFilter === 'all' ? 'mapped' : (effectiveFilter === 'mapped' ? 'unmapped' : 'all');
        if (propSetMappingFilter) propSetMappingFilter(next);
        else setLocalMappingFilter(next);
    };

    // State for "+ Add Field" popover (stores target field name for which popover is active, or '__GHOST__')
    const [activePopoverTarget, setActivePopoverTarget] = useState(null);
    const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0 });
    const [popoverSearch, setPopoverSearch] = useState('');
    const popoverRef = useRef(null);

    // Safe delete UX tracking
    const [deletingTargets, setDeletingTargets] = useState(new Set());
    const [errorTargets, setErrorTargets] = useState(new Set());

    // Persistent Ghost Row state for adding new field mapping
    const [ghostLabel, setGhostLabel] = useState('');
    const [ghostLabelUserEdited, setGhostLabelUserEdited] = useState(false);
    const [ghostSources, setGhostSources] = useState([]);
    const [ghostDelimiter, setGhostDelimiter] = useState(' - ');

    // Light dismiss for column popover (click outside or Esc)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setActivePopoverTarget(null);
                setPopoverSearch('');
            }
        };

        if (activePopoverTarget) {
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePopoverTarget]);

    // Normalize source columns from definitions or props
    const rawCols = definitions?.sourceFields || definitions?.sourceColumns || sourceColumns || [];
    const sourceColumnsList = useMemo(() => {
        return rawCols.map(col => {
            const colName = typeof col === 'string' ? col : (col.name || col.COLUMN_NAME || '');
            const desc = typeof col === 'object' ? (col.description || col.label || '') : '';
            const colType = typeof col === 'object' ? (col.type || 'string') : 'string';
            const isPk = typeof col === 'object' ? (col.isPrimaryKey || false) : false;
            const colLabel = desc && desc !== colName ? `${colName} (${desc})` : colName;
            return { name: colName, label: colLabel, description: desc, type: colType, isPrimaryKey: isPk };
        }).filter(c => c.name);
    }, [rawCols]);

    // Target fields lookup for types, requirements, and labels
    const targetFieldsList = useMemo(() => {
        return definitions?.targetFields || [];
    }, [definitions]);

    const targetFieldsLookup = useMemo(() => {
        const map = new Map();
        targetFieldsList.forEach(tf => {
            map.set(tf.name.toLowerCase(), tf);
        });
        return map;
    }, [targetFieldsList]);

    // Dynamic filtering: Set of already assigned target field names in this pipeline (lowercase)
    const assignedTargetNames = useMemo(() => {
        const set = new Set();
        (mappings || []).forEach(m => {
            if (m && m.target) {
                set.add(m.target.toLowerCase());
            }
        });
        return set;
    }, [mappings]);

    // Pre-calculate target options map to avoid re-allocating arrays per row on every render
    const targetOptionsByTarget = useMemo(() => {
        const unassignedOptions = targetFieldsList
            .filter(tf => !assignedTargetNames.has(tf.name.toLowerCase()))
            .map(tf => ({
                value: tf.name,
                label: tf.label || tf.name,
                description: tf.rules || tf.type,
                isPowered: true
            }));

        const map = new Map();
        targetFieldsList.forEach(tf => {
            const lower = tf.name.toLowerCase();
            if (assignedTargetNames.has(lower)) {
                map.set(lower, [
                    {
                        value: tf.name,
                        label: tf.label || tf.name,
                        description: tf.rules || tf.type,
                        isPowered: true
                    },
                    ...unassignedOptions
                ]);
            } else {
                map.set(lower, unassignedOptions);
            }
        });
        map.set('', unassignedOptions);
        return map;
    }, [targetFieldsList, assignedTargetNames]);

    // Helper to extract live QB sample values
    const getQbSampleValue = (fieldPath) => {
        if (!qbSampleRecords || qbSampleRecords.length === 0) return null;
        const rec = qbSampleRecords[activeQbSampleIndex];
        if (!rec) return null;
        const parts = fieldPath.split('.');
        let val = rec;
        for (const p of parts) {
            if (val == null) break;
            val = val[p];
        }
        return val;
    };

    // Helper to extract live PBS sample values
    const getPbsSampleValue = (fieldNames, template, delimiter) => {
        if (!pbsSampleRecords || pbsSampleRecords.length === 0) return null;
        const rec = pbsSampleRecords[activeSampleIndex];
        if (!rec) return null;
        if (template) {
            let str = template;
            fieldNames.forEach(f => {
                const regex = new RegExp(`\\{${f}\\}`, 'g');
                str = str.replace(regex, rec[f] !== undefined ? String(rec[f]).trim() : '');
            });
            return str;
        }
        const delim = delimiter !== undefined ? delimiter : ' - ';
        const vals = fieldNames
            .map(f => (rec[f] !== undefined && rec[f] !== null) ? String(rec[f]).trim() : '')
            .filter(v => v.length > 0);
        return vals.join(delim);
    };

    // Filter available columns for the active popover
    const filteredPopoverColumns = useMemo(() => {
        if (!popoverSearch.trim()) return sourceColumnsList;
        const q = popoverSearch.toLowerCase().trim();
        return sourceColumnsList.filter(col =>
            col.name.toLowerCase().includes(q) ||
            col.label.toLowerCase().includes(q) ||
            col.description.toLowerCase().includes(q)
        );
    }, [sourceColumnsList, popoverSearch]);

    // Filter rows based on search filter & mapped toggle
    const visibleMappings = useMemo(() => {
        return mappings.filter(m => {
            const assignedSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            const hasSources = assignedSources.length > 0 && assignedSources.some(s => s && s.trim().length > 0);

            if (effectiveFilter === 'mapped' && !hasSources) return false;
            if (effectiveFilter === 'unmapped' && hasSources) return false;

            if (effectiveSearch && effectiveSearch.trim()) {
                const q = effectiveSearch.toLowerCase().trim();
                const labelMatch = (m.label || '').toLowerCase().includes(q);
                const targetMatch = (m.target || '').toLowerCase().includes(q);
                const sourceMatch = assignedSources.some(s => s && s.toLowerCase().includes(q));
                if (!labelMatch && !targetMatch && !sourceMatch) return false;
            }

            return true;
        });
    }, [mappings, effectiveFilter, effectiveSearch]);

    // Commit Ghost Row to local state and reset ghost row
    const commitGhostRow = (targetVal, overrideLabel = null, overrideSources = null) => {
        if (!targetVal || !targetVal.trim()) return;
        const targetClean = targetVal.trim();
        const effectiveLabel = (overrideLabel !== null ? overrideLabel : ghostLabel) || targetClean;
        const effectiveSources = overrideSources !== null ? overrideSources : ghostSources;

        onAddMapping({
            target: targetClean,
            label: effectiveLabel.trim(),
            sources: effectiveSources,
            source: effectiveSources.join(ghostDelimiter),
            delimiter: ghostDelimiter,
            template: ''
        });

        // Reset Ghost Row
        setGhostLabel('');
        setGhostLabelUserEdited(false);
        setGhostSources([]);
        setGhostDelimiter(' - ');
    };

    // Auto-fill logic when user selects Target Field (Col 4) in Ghost Row
    const handleGhostTargetSelect = (chosenTarget) => {
        if (!chosenTarget) return;
        const targetMeta = targetFieldsLookup.get(chosenTarget.toLowerCase());
        const suggestedLabel = targetMeta?.label || chosenTarget;

        // Auto-fill Col 1 (Mapping Label) if user hasn't typed a custom string
        let nextLabel = ghostLabel;
        if (!ghostLabelUserEdited || !ghostLabel.trim()) {
            nextLabel = suggestedLabel;
            setGhostLabel(nextLabel);
        }

        // Commit immediately
        commitGhostRow(chosenTarget, nextLabel);
    };

    // Safe delete handler with pessimistic visual loading state
    const handleDeleteClick = async (target) => {
        const confirm = await useSyncStore.getState().showConfirm({
            title: 'Delete Field Mapping',
            message: 'Are you sure you want to delete this field mapping?',
            confirmText: 'Delete'
        });
        if (!confirm) return;

        setDeletingTargets(prev => new Set(prev).add(target));
        setErrorTargets(prev => {
            const next = new Set(prev);
            next.delete(target);
            return next;
        });
        try {
            await handleRemoveCustomRow(target);
        } catch (err) {
            setErrorTargets(prev => new Set(prev).add(target));
            useSyncStore.getState().showAlert(err.message || 'Failed to delete field mapping');
        } finally {
            setDeletingTargets(prev => {
                const next = new Set(prev);
                next.delete(target);
                return next;
            });
        }
    };

    return (
        <div className={`flex flex-col text-xs ${isStandardMode ? 'bg-transparent' : 'h-full bg-slate-900 overflow-hidden'}`}>
            {/* Live Sample Navigators - Shown in Pipeline Mode */}
            {!isStandardMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-950/70 border-b border-slate-800 shrink-0 text-xs">
                    {/* LEFT: Source ERP Sample Navigator */}
                    <div className="bg-slate-900 border border-emerald-900/50 rounded-xl p-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-emerald-400">
                            <span className="material-symbols-outlined text-[15px]">database</span>
                            <span>Source Record ({activeTableName || 'ERP'})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.max(0, activeSampleIndex - 1))}
                                disabled={activeSampleIndex === 0 || !pbsSampleRecords || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {pbsSampleRecords?.length > 0 ? activeSampleIndex + 1 : 0} / {pbsSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveSampleIndex(Math.min((pbsSampleRecords?.length || 1) - 1, activeSampleIndex + 1))}
                                disabled={!pbsSampleRecords || activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                        </div>
                    </div>

                    {/* RIGHT: Target QuickBooks Sample Navigator */}
                    <div className="bg-slate-900 border border-purple-900/50 rounded-xl p-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-purple-300">
                            <span className="material-symbols-outlined text-[15px]">cloud</span>
                            <span>Target Record ({selectedQBEntity})</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.max(0, activeQbSampleIndex - 1))}
                                disabled={activeQbSampleIndex === 0 || !qbSampleRecords || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&lsaquo;</button>
                            <span className="text-slate-400 mx-1 font-mono text-[11px]">
                                {qbSampleRecords?.length > 0 ? activeQbSampleIndex + 1 : 0} / {qbSampleRecords?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setActiveQbSampleIndex(Math.min((qbSampleRecords?.length || 1) - 1, activeQbSampleIndex + 1))}
                                disabled={!qbSampleRecords || activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            >&rsaquo;</button>
                        </div>
                    </div>
                </div>
            )}

            {!isStandardMode && (
                <div className="flex items-center justify-between gap-3 px-4 py-2 bg-slate-950/80 border-b border-slate-800 shrink-0">
                    <div className="relative w-72">
                        <span className="material-symbols-outlined text-slate-500 absolute left-2.5 top-1.5 text-[15px]">search</span>
                        <input
                            type="text"
                            value={searchFilter}
                            onChange={e => setSearchFilter(e.target.value)}
                            placeholder="Filter fields by name or label..."
                            className="w-full bg-slate-900 border border-slate-750 hover:border-slate-600 rounded-lg pl-8 pr-7 py-1 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                        />
                        {searchFilter && (
                            <button
                                type="button"
                                onClick={() => setSearchFilter('')}
                                className="absolute right-2 top-1 text-slate-500 hover:text-white text-xs font-bold"
                            >&times;</button>
                        )}
                    </div>

                    {/* Filter Pills: All | Mapped | Unmapped */}
                    <div className="flex bg-slate-900 p-0.5 rounded-lg border border-slate-750">
                        <button
                            type="button"
                            onClick={() => setMappingFilter('all')}
                            className={`px-2.5 py-0.5 rounded text-[11px] font-semibold transition-colors ${
                                mappingFilter === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            All ({mappings.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setMappingFilter('mapped')}
                            className={`px-2.5 py-0.5 rounded text-[11px] font-semibold transition-colors ${
                                mappingFilter === 'mapped' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            Mapped ({mappings.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setMappingFilter('unmapped')}
                            className={`px-2.5 py-0.5 rounded text-[11px] font-semibold transition-colors ${
                                mappingFilter === 'unmapped' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            Unmapped ({mappings.filter(m => (!m.sources || m.sources.length === 0) && (!m.source || !m.source.trim())).length})
                        </button>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* 5-COLUMN TREE GRID TABLE HEADER                                        */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            <div 
                ref={stickyHeaderRef}
                className={`grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-7 px-2 border-b border-slate-700/80 text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0 bg-slate-800 ${
                    isStandardMode 
                        ? `sticky top-[128px] z-[30] ${isStuck ? 'shadow-md shadow-black/50 border-transparent transition-shadow duration-200' : 'shadow-none transition-shadow duration-200'}` 
                        : 'border-slate-700'
                }`}
            >
                {/* Col 1 (250px, pl-10): Mapping Label with Ghost Expandable Search */}
                <div className="group flex items-center justify-between pl-10 pr-1 min-w-0">
                    <span className="truncate">Mapping Label</span>
                    {isSearchExpanded || effectiveSearch ? (
                        <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px] text-slate-400">search</span>
                            <input
                                type="text"
                                autoFocus
                                value={effectiveSearch}
                                onChange={e => handleSearchChange(e.target.value)}
                                onBlur={() => {
                                    if (!effectiveSearch.trim()) {
                                        setIsSearchExpanded(false);
                                    }
                                }}
                                placeholder="Search fields..."
                                className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0 h-5 text-[10px] text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none w-28 normal-case font-normal"
                            />
                            {effectiveSearch && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleSearchChange('');
                                        setIsSearchExpanded(false);
                                    }}
                                    className="text-slate-400 hover:text-white text-xs font-bold"
                                >&times;</button>
                            )}
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIsSearchExpanded(true)}
                            className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-750 text-slate-400 hover:text-white"
                            title="Search fields (Click to expand)"
                        >
                            <span className="material-symbols-outlined text-[14px]">search</span>
                        </button>
                    )}
                </div>

                {/* Col 2 (1fr): Source Field */}
                <div>Source Field</div>

                {/* Col 3 (80px): Flow */}
                <div className="text-center">Flow</div>

                {/* Col 4 (1fr): Target Field with Ghost Cycle Funnel */}
                <div className="group flex items-center justify-between pr-1 min-w-0">
                    <span className="truncate">Target Field</span>
                    <button
                        type="button"
                        onClick={handleFilterCycle}
                        className={`p-0.5 rounded transition-all flex items-center gap-0.5 ${
                            effectiveFilter !== 'all'
                                ? 'text-indigo-400 opacity-100 bg-slate-900 border border-indigo-500/50 px-1'
                                : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100 text-slate-400 hover:text-white hover:bg-slate-750'
                        }`}
                        title={`Filter: ${effectiveFilter.toUpperCase()} (Click to cycle All -> Mapped -> Unmapped)`}
                    >
                        <span className="material-symbols-outlined text-[14px]">filter_alt</span>
                        {effectiveFilter !== 'all' && (
                            <span className="text-[9px] font-mono lowercase">{effectiveFilter}</span>
                        )}
                    </button>
                </div>

                {/* Col 5 (70px): Empty header cell */}
                <div className="w-[70px] shrink-0"></div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* MAPPING ROWS LIST (Ultra-condensed h-9 rows, flush border-b)           */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            <div className={isStandardMode ? "p-0 relative z-0 bg-slate-800" : "flex-1 overflow-y-auto p-0 relative z-0"}>
                {visibleMappings.map((mapping, idx) => {
                    const targetMeta = targetFieldsLookup.get((mapping.target || '').toLowerCase());
                    const isRequired = !!targetMeta?.required;
                    const assignedSources = Array.isArray(mapping.sources) ? mapping.sources : (mapping.source ? [mapping.source] : []);
                    const hasAssignedSources = assignedSources.length > 0 && assignedSources.some(s => s && s.trim().length > 0);
                    const isDeleting = deletingTargets.has(mapping.target);
                    const hasDeleteError = errorTargets.has(mapping.target);
                    const pbsVal = isStandardMode ? null : getPbsSampleValue(assignedSources, mapping.template, mapping.delimiter);

                    // Dynamic dropdown options for Target Field (Col 4) strictly excluding other rows' assigned targets
                    const availableTargetOptions = targetOptionsByTarget.get((mapping.target || '').toLowerCase()) || targetOptionsByTarget.get('') || [];

                    return (
                        <div
                            key={`${mapping.target}-${idx}`}
                            className={`grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 border-b transition-colors relative z-0 group ${
                                hasDeleteError
                                    ? 'border-rose-500 bg-rose-950/20'
                                    : 'border-slate-700/40'
                            } ${
                                isDeleting
                                    ? 'opacity-50 text-slate-500 bg-slate-800 pointer-events-none'
                                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                            }`}
                        >
                            {/* ── COL 1 (250px, pl-10): MAPPING LABEL (DOUBLE CLICK TO EDIT) ── */}
                            <div className="pl-10 relative min-w-0">
                                {editingLabelTarget === mapping.target ? (
                                    <input
                                        type="text"
                                        autoFocus
                                        value={tempLabelValue}
                                        onChange={e => setTempLabelValue(e.target.value)}
                                        onBlur={() => {
                                            setEditingLabelTarget(null);
                                            if (tempLabelValue.trim()) {
                                                handleCustomMappingChange(mapping.target, 'label', tempLabelValue.trim());
                                            }
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                setEditingLabelTarget(null);
                                                if (tempLabelValue.trim()) {
                                                    handleCustomMappingChange(mapping.target, 'label', tempLabelValue.trim());
                                                }
                                            } else if (e.key === 'Escape') {
                                                setEditingLabelTarget(null);
                                            }
                                        }}
                                        className="w-full h-7 bg-slate-950 border border-indigo-500 rounded px-2 py-0 text-xs text-white focus:outline-none"
                                    />
                                ) : (
                                    <span
                                        onDoubleClick={() => {
                                            setEditingLabelTarget(mapping.target);
                                            setTempLabelValue(mapping.label !== undefined ? mapping.label : (targetMeta?.label || mapping.target));
                                        }}
                                        className="text-xs text-slate-300 font-medium truncate block cursor-pointer select-none hover:text-indigo-300 transition-colors"
                                        title="Double-click to edit mapping label"
                                    >
                                        {mapping.label !== undefined ? mapping.label : (targetMeta?.label || mapping.target)}
                                    </span>
                                )}
                            </div>

                            {/* ── COL 2 (1fr): SOURCE FIELD BUILDER (TOKENS & [+] ADD FIELD) ── */}
                            <div className="relative min-w-0">
                                <div className="flex items-center gap-1 min-h-[26px] overflow-x-auto">
                                    {/* Assigned Tokens */}
                                    {assignedSources.map((src, sIdx) => (
                                        <span
                                            key={sIdx}
                                            className="inline-flex items-center gap-1 bg-emerald-950/90 border border-emerald-600/70 text-emerald-300 px-1.5 py-0.2 rounded font-mono text-[10px] shrink-0"
                                        >
                                            <span className="font-bold">{src}</span>
                                            <button
                                                type="button"
                                                disabled={isDeleting}
                                                onClick={() => {
                                                    const ns = assignedSources.filter((_, i) => i !== sIdx);
                                                    handleCustomMappingChange(mapping.target, 'sources', ns);
                                                }}
                                                className="text-emerald-400/60 hover:text-rose-400 font-bold ml-0.5 disabled:opacity-50"
                                                title="Remove column"
                                            >&times;</button>
                                        </span>
                                    ))}

                                    {/* Add Field Button (Triggers Portal Popover) */}
                                    <div className="relative inline-block shrink-0">
                                        <button
                                            type="button"
                                            disabled={isDeleting}
                                            onClick={e => {
                                                if (activePopoverTarget === mapping.target) {
                                                    setActivePopoverTarget(null);
                                                    setPopoverSearch('');
                                                } else {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setPopoverCoords({
                                                        top: rect.bottom + 4,
                                                        left: Math.max(10, Math.min(rect.left, window.innerWidth - 300))
                                                    });
                                                    setActivePopoverTarget(mapping.target);
                                                    setPopoverSearch('');
                                                }
                                            }}
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-colors disabled:opacity-50 ${
                                                hasAssignedSources
                                                    ? 'bg-slate-800 hover:bg-slate-750 text-slate-200 border-slate-700'
                                                    : 'bg-indigo-950/40 hover:bg-indigo-900/60 text-indigo-300 border-indigo-800/60'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[12px]">add</span>
                                            <span>{hasAssignedSources ? 'Add' : 'Add Source'}</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Live Source Sample Snippet */}
                                {!isStandardMode && pbsVal && (
                                    <div className="text-[9px] text-emerald-300 font-mono bg-slate-950/70 px-1 py-0.2 rounded border border-slate-800/80 truncate">
                                        <span className="text-slate-500 font-sans mr-1">ERP:</span>
                                        <span>{pbsVal}</span>
                                    </div>
                                )}
                            </div>

                            {/* ── COL 3 (80px, center): DELIMITER / FLOW ➔ ── */}
                            <div className="flex items-center justify-center gap-1 text-center shrink-0">
                                {assignedSources.length > 1 && (
                                    <select
                                        value={mapping.delimiter !== undefined ? mapping.delimiter : ' - '}
                                        onChange={e => handleCustomMappingChange(mapping.target, 'delimiter', e.target.value)}
                                        disabled={isDeleting}
                                        className="h-6 bg-slate-900 border border-slate-700 rounded px-1 py-0 text-[10px] text-indigo-300 font-mono disabled:opacity-50"
                                        title="Delimiter between merged source fields"
                                    >
                                        <option value=" - ">-</option>
                                        <option value=", ">,</option>
                                        <option value=" ">Space</option>
                                        <option value="_">_</option>
                                        <option value="/">/</option>
                                    </select>
                                )}
                                <span className="text-slate-500 font-bold text-xs select-none">➔</span>
                            </div>

                            {/* ── COL 4 (1fr): TARGET FIELD (FILTERED DROPDOWN + REQUIRED BADGE, pr-10 SYMMETRIC INDENT) ── */}
                            <div className="flex items-center gap-1.5 min-w-0 pr-10">
                                <SearchableSelect
                                    options={availableTargetOptions}
                                    value={mapping.target}
                                    onChange={newTargetName => {
                                        if (newTargetName && newTargetName !== mapping.target) {
                                            handleCustomMappingChange(mapping.target, 'target', newTargetName);
                                        }
                                    }}
                                    placeholder="Select target field..."
                                    className="w-full"
                                    disabled={isDeleting}
                                />

                                {isRequired && (
                                    <span className="px-1 py-0.2 bg-rose-950/80 text-rose-300 border border-rose-800 rounded text-[9px] font-bold shrink-0" title="Required by target schema">
                                        * Req
                                    </span>
                                )}

                                {targetMeta?.type && (
                                    <span className="px-1 py-0.2 bg-slate-900 text-slate-400 border border-slate-800 rounded font-mono text-[9px] shrink-0">
                                        {targetMeta.type}
                                    </span>
                                )}
                            </div>

                            {/* ── COL 5 (70px): INLINE GHOST ACTIONS ([RESET] & [DELETE]) ── */}
                            <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex gap-1.5 items-center justify-end pr-1">
                                <button
                                    type="button"
                                    disabled={isDeleting}
                                    onClick={() => handleResetMappingRow(mapping.target)}
                                    className="h-6 w-6 rounded hover:bg-slate-700/60 text-slate-400 hover:text-white flex items-center justify-center transition-colors disabled:opacity-40 focus:outline-none"
                                    title="Reset mapping assignment"
                                >
                                    <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleDeleteClick(mapping.target)}
                                    disabled={isDeleting || (isRequired && hasAssignedSources)}
                                    className={`h-6 w-6 rounded hover:bg-rose-950/60 flex items-center justify-center transition-colors focus:outline-none ${
                                        isDeleting
                                            ? 'text-slate-500 cursor-wait'
                                            : isRequired && hasAssignedSources
                                                ? 'text-slate-600 cursor-not-allowed opacity-30'
                                                : 'text-slate-400 hover:text-rose-400'
                                    }`}
                                    title={isRequired && hasAssignedSources ? "Required field cannot be deleted while assigned" : "Delete field mapping row"}
                                >
                                    {isDeleting ? (
                                        <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                    ) : (
                                        <span className="material-symbols-outlined text-[14px]">delete</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    );
                })}

                {/* ══════════════════════════════════════════════════════════════════ */}
                {/* PERSISTENT TIER 3 GHOST ROW (OPT-IN DATA ENTRY)                    */}
                {/* ══════════════════════════════════════════════════════════════════ */}
                {(() => {
                    // Available targets for Ghost Row strictly exclude any currently assigned fields
                    const ghostTargetOptions = targetOptionsByTarget.get('') || [];

                    return (
                        <div className={`grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 border-b border-dashed border-slate-700/60 bg-slate-800 hover:bg-slate-700 transition-colors relative z-0 group`}>
                            {/* Col 1 (250px, pl-10): Ghost Label input */}
                            <div className="pl-10 relative">
                                <input
                                    type="text"
                                    value={ghostLabel}
                                    onChange={e => {
                                        setGhostLabel(e.target.value);
                                        setGhostLabelUserEdited(true);
                                    }}
                                    placeholder="+ New mapping label..."
                                    className="w-full h-7 bg-slate-950/80 border border-slate-750 hover:border-slate-600 rounded px-2 py-0 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                                />
                            </div>

                            {/* Col 2 (1fr): Ghost Source Field Builder */}
                            <div className="relative min-w-0">
                                <div className="flex items-center gap-1 min-h-[26px] overflow-x-auto">
                                    {ghostSources.map((src, sIdx) => (
                                        <span
                                            key={sIdx}
                                            className="inline-flex items-center gap-1 bg-emerald-950/90 border border-emerald-600/70 text-emerald-300 px-1.5 py-0.2 rounded font-mono text-[10px] shrink-0"
                                        >
                                            <span className="font-bold">{src}</span>
                                            <button
                                                type="button"
                                                onClick={() => setGhostSources(ghostSources.filter((_, i) => i !== sIdx))}
                                                className="text-emerald-400/60 hover:text-rose-400 font-bold ml-0.5"
                                            >&times;</button>
                                        </span>
                                    ))}

                                    <div className="relative inline-block shrink-0">
                                        <button
                                            type="button"
                                            onClick={e => {
                                                if (activePopoverTarget === '__GHOST__') {
                                                    setActivePopoverTarget(null);
                                                    setPopoverSearch('');
                                                } else {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setPopoverCoords({
                                                        top: rect.bottom + 4,
                                                        left: Math.max(10, Math.min(rect.left, window.innerWidth - 300))
                                                    });
                                                    setActivePopoverTarget('__GHOST__');
                                                    setPopoverSearch('');
                                                }
                                            }}
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium bg-slate-800 hover:bg-slate-750 text-slate-300 border-slate-700 transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">add</span>
                                            <span>Add Source</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Col 3 (80px, center): Delimiter / Flow */}
                            <div className="flex items-center justify-center gap-1 text-center shrink-0">
                                {ghostSources.length > 1 && (
                                    <select
                                        value={ghostDelimiter}
                                        onChange={e => setGhostDelimiter(e.target.value)}
                                        className="h-6 bg-slate-900 border border-slate-700 rounded px-1 py-0 text-[10px] text-indigo-300 font-mono"
                                        title="Delimiter between merged source fields"
                                    >
                                        <option value=" - ">-</option>
                                        <option value=", ">,</option>
                                        <option value=" ">Space</option>
                                        <option value="_">_</option>
                                        <option value="/">/</option>
                                    </select>
                                )}
                                <span className="text-slate-600 font-bold text-xs select-none">➔</span>
                            </div>

                            {/* Col 4 (1fr): Target Field Dropdown (pr-10 SYMMETRIC INDENT) */}
                            <div className="min-w-0 pr-10">
                                <SearchableSelect
                                    options={ghostTargetOptions}
                                    value=""
                                    onChange={handleGhostTargetSelect}
                                    placeholder="+ Select destination field..."
                                    className="w-full"
                                />
                            </div>

                            {/* Col 5 (70px): Ghost Column Placeholder */}
                            <div className="w-[70px] shrink-0" />
                        </div>
                    );
                })()}
            </div>

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* PORTAL: FLOATING SOURCE FIELD BUILDER POPOVER (zIndex: 9999)           */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            {activePopoverTarget && createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: 'fixed',
                        top: `${popoverCoords.top}px`,
                        left: `${popoverCoords.left}px`,
                        zIndex: 9999
                    }}
                    className="w-72 bg-slate-950 border border-slate-700 rounded-lg shadow-2xl p-2 space-y-1.5 animate-in fade-in zoom-in-95 duration-100"
                >
                    <div className="relative">
                        <span className="material-symbols-outlined text-slate-400 text-[14px] absolute left-2 top-2">search</span>
                        <input
                            type="text"
                            autoFocus
                            value={popoverSearch}
                            onChange={e => setPopoverSearch(e.target.value)}
                            placeholder="Search column..."
                            className="w-full h-7 bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-0 text-xs text-white focus:outline-none focus:border-indigo-500 font-mono"
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y divide-slate-800/40 text-[11px]">
                        {filteredPopoverColumns.length === 0 ? (
                            <div className="p-2 text-center text-slate-500 italic">No columns found</div>
                        ) : (
                            filteredPopoverColumns.map(col => (
                                <button
                                    key={col.name}
                                    type="button"
                                    onClick={() => {
                                        if (activePopoverTarget === '__GHOST__') {
                                            if (!ghostSources.includes(col.name)) {
                                                const updated = [...ghostSources, col.name];
                                                setGhostSources(updated);
                                            }
                                        } else {
                                            const currentMapping = mappings.find(m => (m.target || '').toLowerCase() === activePopoverTarget.toLowerCase());
                                            const currentSources = currentMapping
                                                ? (Array.isArray(currentMapping.sources) ? currentMapping.sources : (currentMapping.source ? [currentMapping.source] : []))
                                                : [];
                                            if (!currentSources.includes(col.name)) {
                                                const ns = [...currentSources, col.name];
                                                handleCustomMappingChange(activePopoverTarget, 'sources', ns);
                                            }
                                        }
                                        setActivePopoverTarget(null);
                                        setPopoverSearch('');
                                    }}
                                    className="w-full text-left px-2 py-1 hover:bg-slate-800/80 rounded flex flex-col gap-0.5 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono font-bold text-emerald-400">{col.name}</span>
                                        {col.isPrimaryKey && (
                                            <span className="text-[9px] px-1 py-0.2 bg-amber-950 text-amber-400 rounded border border-amber-800 font-mono">PK</span>
                                        )}
                                    </div>
                                    {col.description && (
                                        <span className="text-[10px] text-slate-400 truncate">{col.description}</span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
